import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"

// Editor for a billing document's text segments (Einleitungs-/Schlusstext).
// One controller instance wraps the "Texte" section; each group
// (data-position="intro|closing") holds its own list, template and picker popover.
//
// Segments are nested-attribute sub-forms, so nothing is fetched on add or
// reorder — the rows travel with the surrounding document form. The picker
// popover IS fetched on every open with the current form context (customer,
// dates, ...) so placeholder resolution matches the unsaved form state; rows
// carry their resolved text plus the block's output target and BT-21 subject
// code, which the new sub-form is pre-filled with. The picker only lists blocks
// released for the document type and groups them by category; rows can be
// ticked and inserted together (in display order) via its footer button.
export default class extends Controller {
  static targets = [
    "group",
    "list",
    "template",
    "segment",
    "empty",
    "popover",
    "textarea",
    "warning",
    "positionInput",
    "destroyInput",
    "targetInput",
    "subjectCodeInput",
    "handle",
    "filter",
    "row",
    "pickerGroup",
    "pick",
    "pickButton",
    "pickFooter",
    "pickCount",
  ]
  static values = {
    pickerUrl: String,
    resolveUrl: String,
    documentId: Number,
    documentType: String,
  }

  // Mirrors Billing::TextBlockRenderer::TOKEN_PATTERN: plain tokens plus the
  // dotted Zusatzfeld tokens ({{baustelle.bauherr}}).
  static TOKEN_PATTERN = /\{\{\s*([a-z_0-9]+(?:\.[a-z][a-z0-9_]*)?)\s*\}\}/

  // The assignments placeholder values depend on — a change re-resolves them.
  static ASSIGNMENT_FIELDS = ["customer_id", "site_id", "task_id"]

  connect() {
    this.boundOutsideClick = this.outsideClick.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener("click", this.boundOutsideClick)
    document.addEventListener("keydown", this.boundKeydown)
    this.observeAssignmentSelects()
    this.sortables = this.listTargets.map((list) => this.initSortable(list))
    this.textareaTargets.forEach((textarea) => this.updateWarning(textarea))
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick)
    document.removeEventListener("keydown", this.boundKeydown)
    this.assignmentSelects.forEach((select) =>
      select.removeEventListener("change", this.boundAssignmentChange),
    )
    this.resolveRequest?.abort()
    this.sortables.forEach((sortable) => sortable.destroy())
  }

  // ---- popover ----

  toggle(event) {
    const group = event.currentTarget.closest("[data-position]")
    const popover = this.popoverFor(group)
    if (!popover) return

    if (popover.classList.contains("hidden")) {
      this.closeAll()
      this.open(group, popover)
    } else {
      this.closeAll()
    }
  }

  async open(group, popover) {
    popover.innerHTML = ""
    popover.classList.remove("hidden")
    const params = new URLSearchParams({ position: group.dataset.position, ...this.formContext() })
    const response = await fetch(`${this.pickerUrlValue}?${params}`, {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    })
    if (!response.ok) {
      popover.classList.add("hidden")
      return
    }
    popover.innerHTML = await response.text()
    const filter = popover.querySelector("[data-text-segments-target='filter']")
    if (filter) filter.focus()
  }

  closeAll() {
    this.popoverTargets.forEach((popover) => popover.classList.add("hidden"))
  }

  outsideClick(event) {
    if (this.element.contains(event.target)) return
    this.closeAll()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.closeAll()
  }

  // Hides non-matching rows, and every category whose rows are all hidden.
  filter(event) {
    const query = event.currentTarget.value.trim().toLowerCase()
    this.rowTargets.forEach((row) => {
      row.classList.toggle("hidden", query !== "" && !row.dataset.searchText.includes(query))
    })
    this.pickerGroupTargets.forEach((group) => {
      const rows = this.rowTargets.filter((row) => group.contains(row))
      group.classList.toggle(
        "hidden",
        rows.every((row) => row.classList.contains("hidden")),
      )
    })
  }

  // ---- multi-select ----

  // Each position has its own popover, and a closed one keeps its rows — so
  // every pick lookup is scoped to the group the event came from.
  togglePick(event) {
    const group = event.currentTarget.closest("[data-position]")
    const count = this.checkedPicks(group).length
    const counter = this.pickCountTargets.find((target) => group.contains(target))
    const footer = this.pickFooterTargets.find((target) => group.contains(target))
    if (counter) counter.textContent = count
    if (footer) footer.classList.toggle("hidden", count === 0)
  }

  checkedPicks(group) {
    return this.pickTargets.filter((pick) => pick.checked && group.contains(pick))
  }

  // Inserts every ticked row in display order, each as its own segment — not in
  // the order the boxes were ticked, so the result reads like the picker list.
  insertSelected(event) {
    const group = event.currentTarget.closest("[data-position]")
    const buttons = this.checkedPicks(group)
      .map((pick) => this.pickButtonTargets.find((button) => pick.parentElement.contains(button)))
      .filter(Boolean)
    if (buttons.length === 0) return

    buttons.forEach((button) => this.insertFrom(button, group))
    this.closeAll()
    this.refreshGroup(group)
  }

  // ---- adding segments ----

  // From the picker: a new sub-form pre-filled with the block's resolved text,
  // its output target, subject code and a backreference to the block.
  //
  // The prefill MUST stay synchronous with appendSegment: Stimulus connects the
  // row's `select` controller in a later microtask, so SlimSelect initializes
  // from the already-set subject-code value. Deferring any setValue past that
  // connect would leave the SlimSelect UI showing the stale placeholder.
  insert(event) {
    const group = event.currentTarget.closest("[data-position]")
    if (!this.insertFrom(event.currentTarget, group)) return

    this.closeAll()
    this.refreshGroup(group)
  }

  insertFrom(button, group) {
    const { content, outputTarget, subjectCode, textBlockId, title } = button.dataset
    const segment = this.appendSegment(group)
    if (!segment) return null

    this.setValue(segment, "textarea", content)
    this.setValue(segment, "subjectCodeInput", subjectCode || "")
    this.setValue(segment, "[name*='[text_block_id]']", textBlockId || "")
    this.checkTarget(segment, outputTarget)
    this.setSource(segment, title)
    return segment
  }

  addBlank(event) {
    const group = event.currentTarget.closest("[data-position]")
    const segment = this.appendSegment(group)
    if (!segment) return

    this.refreshGroup(group)
    const textarea = segment.querySelector("[data-text-segments-target='textarea']")
    if (textarea) textarea.focus()
  }

  appendSegment(group) {
    const template = this.templateFor(group)
    const list = this.listFor(group)
    if (!template || !list) return null

    const html = template.innerHTML.replaceAll(/NEW_RECORD/g, this.nextIndex())
    list.insertAdjacentHTML("beforeend", html)
    return list.lastElementChild
  }

  // The child index MUST stay all-digits: strong parameters only recognise nested
  // attributes by numeric keys and silently drop every other row. Timestamp plus a
  // per-instance counter, concatenated (not added) so no two rows can collide —
  // timestamps are fixed-length, which keeps the concatenation unambiguous.
  nextIndex() {
    this.counter = (this.counter || 0) + 1
    return `${Date.now()}${this.counter}`
  }

  remove(event) {
    const segment = event.currentTarget.closest("[data-text-segments-target='segment']")
    const group = segment.closest("[data-position]")
    if (segment.dataset.newRecord === "true") {
      segment.remove()
    } else {
      const destroyInput = segment.querySelector("[data-text-segments-target='destroyInput']")
      if (destroyInput) destroyInput.value = "1"
      segment.classList.add("hidden")
    }
    this.refreshGroup(group)
  }

  // ---- ordering ----

  initSortable(list) {
    return new Sortable(list, {
      draggable: "[data-text-segments-target='segment']",
      handle: "[data-text-segments-target='handle']",
      animation: 150,
      ghostClass: "bg-ghost",
      onEnd: () => this.renumber(list),
    })
  }

  // Positions ride along as hidden inputs — no request, the form submit carries them.
  renumber(list) {
    let index = 0
    list.querySelectorAll("[data-text-segments-target='segment']").forEach((segment) => {
      const destroyInput = segment.querySelector("[data-text-segments-target='destroyInput']")
      if (destroyInput && destroyInput.value === "1") return

      index += 1
      const positionInput = segment.querySelector("[data-text-segments-target='positionInput']")
      if (positionInput) positionInput.value = index
    })
  }

  refreshGroup(group) {
    const list = this.listFor(group)
    if (list) this.renumber(list)
    const empty = this.emptyFor(group)
    if (empty) empty.classList.toggle("hidden", this.visibleSegments(group).length > 0)
    this.textareaTargets.forEach((textarea) => this.updateWarning(textarea))
  }

  visibleSegments(group) {
    return [...group.querySelectorAll("[data-text-segments-target='segment']")].filter(
      (segment) => {
        const destroyInput = segment.querySelector("[data-text-segments-target='destroyInput']")
        return !destroyInput || destroyInput.value !== "1"
      },
    )
  }

  // ---- token warning ----

  handleInput(event) {
    this.updateWarning(event.currentTarget)
  }

  updateWarning(textarea) {
    const segment = textarea.closest("[data-text-segments-target='segment']")
    const warning = segment && segment.querySelector("[data-text-segments-target='warning']")
    if (!warning) return
    warning.classList.toggle("hidden", !this.constructor.TOKEN_PATTERN.test(textarea.value))
  }

  // ---- assignment-change re-resolution ----

  observeAssignmentSelects() {
    const form = this.formElement()
    this.assignmentSelects = form
      ? this.constructor.ASSIGNMENT_FIELDS.map((field) =>
          form.querySelector(`[name$='[${field}]']`),
        ).filter(Boolean)
      : []
    this.boundAssignmentChange = this.assignmentChanged.bind(this)
    this.assignmentSelects.forEach((select) =>
      select.addEventListener("change", this.boundAssignmentChange),
    )
  }

  // One user action can fire several changes in a row (customer-site-filter sets
  // the site programmatically), each sent with a different form state. Only the
  // newest request may write: a replaced token is gone for good, so a stale
  // reply arriving last would stick.
  async assignmentChanged() {
    const tokens = this.pendingTokens()
    if (tokens.length === 0) return

    this.resolveRequest?.abort()
    const request = new AbortController()
    this.resolveRequest = request

    const params = new URLSearchParams(this.formContext())
    tokens.forEach((token) => params.append("tokens[]", token))
    let values
    try {
      const response = await fetch(`${this.resolveUrlValue}?${params}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: request.signal,
      })
      if (!response.ok) return
      values = (await response.json()).tokens
    } catch (error) {
      if (error.name === "AbortError") return
      throw error
    }
    if (this.resolveRequest !== request) return

    this.textareaTargets.forEach((textarea) => {
      Object.entries(values).forEach(([token, value]) => {
        if (value) this.replaceToken(textarea, token, value)
      })
      this.updateWarning(textarea)
    })
  }

  pendingTokens() {
    const pattern = new RegExp(this.constructor.TOKEN_PATTERN.source, "g")
    const tokens = new Set()
    this.textareaTargets.forEach((textarea) => {
      for (const match of textarea.value.matchAll(pattern)) tokens.add(match[1])
    })
    return [...tokens]
  }

  // Replaces every literal {{token}} occurrence, preserving the undo stack.
  // Resumes the search after each inserted value so a value that itself
  // contains the token pattern cannot loop forever.
  replaceToken(textarea, token, value) {
    const text = String(value)
    const pattern = new RegExp(`\\{\\{\\s*${token.replace(".", "\\.")}\\s*\\}\\}`, "g")
    let match
    while ((match = pattern.exec(textarea.value))) {
      textarea.focus()
      textarea.setSelectionRange(match.index, match.index + match[0].length)
      if (!document.execCommand("insertText", false, text)) {
        textarea.setRangeText(text, match.index, match.index + match[0].length, "end")
        textarea.dispatchEvent(new Event("input", { bubbles: true }))
      }
      pattern.lastIndex = match.index + text.length
    }
  }

  // ---- helpers ----

  setValue(segment, target, value) {
    const selector = target.startsWith("[") ? target : `[data-text-segments-target='${target}']`
    const input = segment.querySelector(selector)
    if (input) input.value = value
  }

  // The template renders the neutral "Eigener Text" label; a picked block names itself.
  setSource(segment, title) {
    if (!title) return
    const source = segment.querySelector("[data-text-segments-target='source']")
    if (!source) return
    source.textContent = title
    source.classList.replace("text-gray-400", "text-gray-500")
  }

  checkTarget(segment, outputTarget) {
    if (!outputTarget) return
    const radio = segment.querySelector(
      `[data-text-segments-target='targetInput'][value='${outputTarget}']`,
    )
    if (radio) radio.checked = true
  }

  popoverFor(group) {
    return this.popoverTargets.find((target) => group && group.contains(target))
  }

  listFor(group) {
    return this.listTargets.find((target) => group && group.contains(target))
  }

  templateFor(group) {
    return this.templateTargets.find((target) => group && group.contains(target))
  }

  emptyFor(group) {
    return this.emptyTargets.find((target) => group && group.contains(target))
  }

  formElement() {
    return this.element.closest("form")
  }

  // Live form state for server-side placeholder resolution. Field names are the
  // same across the offer and invoice forms (name$="[customer_id]" etc.).
  formContext() {
    const context = {}
    if (this.hasDocumentIdValue) context.document_id = this.documentIdValue
    if (this.hasDocumentTypeValue) context.document_type = this.documentTypeValue
    const form = this.formElement()
    if (!form) return context

    const fields = [
      ...this.constructor.ASSIGNMENT_FIELDS,
      "issue_date",
      "valid_until",
      "service_date_from",
      "service_date_to",
      "payment_terms_days",
    ]
    fields.forEach((field) => {
      const input = form.querySelector(`[name$='[${field}]']`)
      if (input) context[field] = input.value
    })
    return context
  }
}
